import ThumbsUpDownOutlinedIcon from '@mui/icons-material/ThumbsUpDownOutlined';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { NewChip } from './new-chip';
import { SkillsSection } from './skills-section';
import { InfoCard } from '@/components/shared/info-card';
import { UserAvatar } from '@/components/shared/user-avatar';
import { NewFeedbackCard } from '@/packages/home/components/user-profile/new-feedback-card';
import type { UserProfileData } from '@/packages/home/components/user-profile/use-user-profile-data';
import { VideoRecommendationSection } from '@/packages/home/components/video-recommendation-section';
import { lineClamp } from '@/utils/line-clamp';
import { profileGoalDescription } from '@/utils/profile-goal-description';

const INFO_MISSING_MESSAGE = 'Não informado';

const ProfileLabel = ({ label, value }: { label: string; value: string }) => (
  <Typography component="span" color="text.secondary">
    {label}:{' '}
    <Typography component="span" fontWeight="bold">
      {value}
    </Typography>
  </Typography>
);

export const AgentProfileLayout = ({
  jobs,
  userFeedback,
  recommendationProfile,
  profile,
  isProfileLoading,
}: UserProfileData) => {
  const navigate = useNavigate();
  const hasFeedback = userFeedback?.content;
  const FeedbackCard = hasFeedback ? InfoCard : NewFeedbackCard;

  const currentJob =
    jobs?.current?.name ||
    recommendationProfile?.current_job?.name ||
    INFO_MISSING_MESSAGE;
  const goal =
    (recommendationProfile &&
      profileGoalDescription(recommendationProfile, jobs)) ||
    INFO_MISSING_MESSAGE;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <IconButton onClick={() => navigate('/meu-perfil')}>
          <UserAvatar
            name={profile?.name || ''}
            size={64}
            imageSource={profile?.avatar}
            loading={isProfileLoading}
          />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h1">Olá, {profile?.first_name}</Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            columnGap={2}
            rowGap={0.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            flexWrap="wrap"
            mt={0.5}
          >
            <ProfileLabel label="Cargo" value={currentJob} />
            <ProfileLabel label="Objetivo" value={goal} />
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/meu-perfil')}
              sx={{ px: 0, minWidth: 'auto' }}
            >
              Ver perfil
            </Button>
          </Stack>
        </Box>
      </Stack>

      <Grid container spacing={2} alignItems={{ xs: 'start', md: 'stretch' }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FeedbackCard
            icon={ThumbsUpDownOutlinedIcon}
            iconSpacing={4}
            contentMaxWidth="none"
            onClick={() => {
              navigate(
                `/gestao-de-habilidades${!hasFeedback ? '?novo-feedback=true' : ''}`
              );
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h3">Feedback</Typography>
              {!hasFeedback && <NewChip />}
            </Stack>
            {hasFeedback ? (
              <Typography sx={lineClamp(3)}>{userFeedback?.content}</Typography>
            ) : (
              <Typography mt={0.5}>
                Envie seu feedback e receba recomendações personalizadas
              </Typography>
            )}
          </FeedbackCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SkillsSection compact />
        </Grid>
      </Grid>

      <VideoRecommendationSection />
    </Stack>
  );
};
